// =====================================================================
// src/topo.js — os dois botões do canto: o sino e o sol/lua
//
// ATENÇÃO, E ISTO É UMA CORREÇÃO DE ROTA: o `#top-controls` e o modo
// escuro JÁ EXISTIAM neste app (index.html linhas 76 e 170, e a lógica no
// fim de src/main.js). Eu comecei escrevendo um segundo conjunto ao lado,
// com outro id e outra classe, e a prova de navegador acusou dois
// `#btn-tema` na página. Desfeito: o que entra aqui é o que FALTAVA — o
// sino e a troca do ícone sol/lua —, em cima do que já estava de pé.
// É a regra do CLAUDE.md aplicada ao próprio app, não só ao MdM: antes de
// escrever, procure o que já existe.
//
// A DECISÃO está em `src/lib/topo.js`, que não toca em DOM e por isso é
// provada em node. Aqui fica o que só existe no navegador: guardar o tema,
// pintar o badge, montar a lista. Essa separação é a mesma do resto do app.
//
// DE ONDE VÊM OS AVISOS: da função `avisos()` do banco (0035), que deriva
// na hora e já respeita quem está logado. Não há tabela de notificação,
// não há lido/não lido, e nada disso sai do app — igual ao MdM, e dito
// assim ao Hugo antes de construir.
// =====================================================================
import { el, abrirModal } from './ui.js';
import { iconeDoTema, textoDoBadge, ordenar, rotuloDaData, resumoDoHistorico } from './lib/topo.js';

export function montarTopo({ auth, documento = document } = {}) {
  const controles = documento.getElementById('top-controls');
  const btnTema   = documento.getElementById('btn-tema');
  const usoTema   = documento.getElementById('btn-tema-uso');
  const btnAvisos = documento.getElementById('btn-avisos');
  const contador  = documento.getElementById('aviso-contador');
  const painel    = documento.getElementById('painel-avisos');
  if (!controles || !btnTema || !btnAvisos) return null;

  // O CLIQUE do tema já está ligado em src/main.js, e continua lá: o que
  // falta é o ÍCONE acompanhar. Observando a classe do body em vez de
  // escutar o clique, os dois não brigam por quem manda no tema.
  function pintarIcone() {
    const tema = documento.body.classList.contains('dark-mode') ? 'escuro' : 'claro';
    if (usoTema) usoTema.setAttribute('href', '#' + iconeDoTema(tema));
  }
  pintarIcone();
  new MutationObserver(pintarIcone).observe(documento.body, { attributes: true, attributeFilter: ['class'] });

  function pintarBadge(quantos) {
    const texto = textoDoBadge(quantos);
    if (!contador) return;
    contador.textContent = texto;
    contador.hidden = texto === '';
  }

  // O sino não pode derrubar a tela. Se `avisos()` falhar — rede caiu,
  // sessão expirou —, o badge some e a vida segue: aviso é acessório, e
  // acessório que quebra o principal é defeito.
  async function buscar() {
    try { return ordenar(await auth.rpc('avisos')); }
    catch { return null; }
  }

  async function atualizar() {
    const lista = await buscar();
    pintarBadge(lista ? lista.length : 0);
    return lista;
  }

  const bolinha = cor => el('span', {
    style: { width: '8px', height: '8px', borderRadius: '999px', marginTop: '5px',
             background: cor || '#6b7280', flex: '0 0 auto' } });

  function desenhar(lista) {
    painel.textContent = '';
    if (!lista || lista.length === 0) {
      painel.appendChild(el('div', { class: 'nada' }, 'Tudo em dia. Nenhum aviso.'));
    } else {
      painel.appendChild(el('div', { class: 'cabeca' }, `Avisos (${lista.length})`));
      for (const a of lista) {
        painel.appendChild(el('div', { class: 'item' },
          bolinha(a.cor),
          el('div', {},
            el('div', { class: 'txt' }, a.titulo || ''),
            a.sub ? el('div', { class: 'sub' }, a.sub) : null)));
      }
    }
    // O rodapé aparece SEMPRE, inclusive com o painel vazio: é justamente
    // quem não tem nada pendente que vai querer conferir o que passou.
    painel.appendChild(el('button', { class: 'pe', id: 'btn-historico', type: 'button' },
      'Ver os últimos 30 dias'));
  }

  // ── A ABA DOS ÚLTIMOS 30 DIAS (0036) ───────────────────────────────
  // Decisão do Hugo, 19/09: ela mora no painel do sino, não no menu. O menu
  // deste app é montado a partir dos módulos CONTRATADOS; um item que não é
  // módulo abriria exceção na regra, e o histórico não é coisa que se
  // contrate — é o mesmo aviso do sino, com data.
  //
  // O componente é o `abrirModal` do ui.js, que já existe e já fecha no Esc
  // e no clique fora. Não escrevi sobreposição nova: é a regra do CLAUDE.md
  // 4.2, e foi ela que me pegou no modo escuro duplicado.
  function linhaDoHistorico(h) {
    return el('div', { class: 'hist-item' },
      bolinha(h.cor),
      el('div', { style: { flex: '1 1 auto', minWidth: '0' } },
        el('div', { class: 'txt' }, h.titulo || ''),
        h.sub ? el('div', { class: 'sub' }, h.sub) : null),
      el('div', { class: 'quando' }, rotuloDaData(h.quando)));
  }

  async function abrirHistorico() {
    painel.hidden = true;
    const corpo = el('div', { id: 'lista-historico' }, el('div', { class: 'nada' }, 'Carregando…'));
    abrirModal(el('div', {},
      el('div', { class: 'modal-title' }, 'Notificações'),
      el('div', { id: 'historico-resumo', class: 'sub' }, ''),
      corpo), { largura: '620px' });

    let lista = null;
    try { lista = await auth.rpc('historico_de_avisos'); } catch { lista = null; }

    // A modal pode ter sido fechada enquanto a resposta vinha. Escrever num
    // nó que saiu da página não quebra, mas confundir o Esc da pessoa com
    // "não carregou" quebra a confiança dela na tela.
    if (!documento.getElementById('lista-historico')) return;
    corpo.textContent = '';
    const resumoEl = documento.getElementById('historico-resumo');
    if (lista === null) {
      if (resumoEl) resumoEl.textContent = '';
      corpo.appendChild(el('div', { class: 'nada' },
        'Não consegui buscar agora. Feche e abra de novo.'));
      return;
    }
    if (resumoEl) resumoEl.textContent = resumoDoHistorico(lista);
    if (lista.length === 0) {
      corpo.appendChild(el('div', { class: 'nada' }, 'Nenhuma notificação nos últimos 30 dias.'));
      return;
    }
    for (const h of lista) corpo.appendChild(linhaDoHistorico(h));
  }

  btnAvisos.addEventListener('click', async () => {
    if (!painel.hidden) { painel.hidden = true; return; }
    desenhar(await atualizar());
    painel.hidden = false;
  });

  // Delegado no painel porque o rodapé é redesenhado a cada abertura: um
  // ouvinte por botão novo deixaria um ouvinte morto por abertura.
  painel.addEventListener('click', ev => {
    if (ev.target && ev.target.id === 'btn-historico') abrirHistorico();
  });

  // Clique fora fecha, como o modal do `ui.js`. Sem isto o painel fica
  // aberto por cima da tela e a pessoa clica duas vezes para voltar.
  documento.addEventListener('click', ev => {
    if (painel.hidden) return;
    if (painel.contains(ev.target) || btnAvisos.contains(ev.target)) return;
    painel.hidden = true;
  });

  btnAvisos.hidden = false;   // o sino só aparece dentro do app

  // AQUI NÃO SE BUSCA. Quem manda atualizar é o `main.js`, em toda entrada
  // no app — e ele já faz isso na primeira, logo depois de montar. Um
  // `atualizar()` também aqui significava DUAS chamadas a `avisos()` por
  // entrada, todas as vezes, para o mesmo resultado. Ninguém via, porque o
  // badge fica igual; quem viu foi `prova_tela_offline.mjs`, contando o que
  // a tela pede à rede. Dois donos da mesma decisão é como isso nasce.
  return { atualizar };
}
